import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { faArrowLeft, faArrowRight, faTicket } from '@fortawesome/free-solid-svg-icons';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { EnumLanguage } from '@simplyfly/shared';
import { getLanguageLabel } from 'src/app/enums/language.helpers';
import { EnumEnquiryFlightBoardingPass, EnumEnquiryFlightSeatingPlan, IEnquiryFlight } from '@simplyfly/shared';
import { getEnquiryFlightBoardingPassLabel, getEnquiryFlightSeatingPlanLabel } from 'src/app/interfaces/enquiry-flight.helpers';
import { IEnquiry } from '@simplyfly/shared';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

@Component({
    selector: 'app-enquiry-flight-edit-details',
    templateUrl: './enquiry-flight-edit-details.component.html',
    styleUrls: ['./enquiry-flight-edit-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [FormsModule, ReactiveFormsModule, FaIconComponent, TranslateModule]
})
export class EnquiryFlightEditDetailsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() enquiry: IEnquiry | null = null;
  @Input() enquiryFlight: IEnquiryFlight | null = null;
  @Output() goPrev: EventEmitter<void> = new EventEmitter();
  @Output() saveAndGoNext: EventEmitter<IEnquiryFlight> = new EventEmitter();

  form: FormGroup = this.resetForm();

  EnumEnquiryFlightSeatingPlan = EnumEnquiryFlightSeatingPlan;
  EnumEnquiryFlightBoardingPass = EnumEnquiryFlightBoardingPass;

  getLanguageLabel = getLanguageLabel;
  getEnquiryFlightSeatingPlanLabel = getEnquiryFlightSeatingPlanLabel;
  getEnquiryFlightBoardingPassLabel = getEnquiryFlightBoardingPassLabel;

  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faTicket = faTicket;

  translationObj: { [key: string]: any } = {};

  subscriptionsTranslations = new Subscription();

  constructor(private translateService: TranslateService) {}

  ngOnInit(): void {
    this.loadTranslation();

    this.applyEnquiryFlight();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.enquiryFlight &&
      (!changes.enquiryFlight.previousValue ||
        changes.enquiryFlight.previousValue !== changes.enquiryFlight.currentValue)
    ) {
      this.applyEnquiryFlight();
    }
  }

  ngOnDestroy(): void {

    this.subscriptionsTranslations.unsubscribe();
  }

  getSeatingPlans(): EnumEnquiryFlightSeatingPlan[] {
    return Object.values(EnumEnquiryFlightSeatingPlan);
  }

  getBoardingPasses(): EnumEnquiryFlightBoardingPass[] {
    return Object.values(EnumEnquiryFlightBoardingPass);
  }

  get isFerryFlight(): FormControl {
    return this.form.get('isFerryFlight') as FormControl;
  }

  get flightNumber(): FormControl {
    return this.form.get('flightNumber') as FormControl;
  }

  get callsign(): FormControl {
    return this.form.get('callsign') as FormControl;
  }

  get otherComment(): FormControl {
    return this.form.get('otherComment') as FormControl;
  }
  get checkInScreenMessage(): FormControl {
    return this.form.get('checkInScreenMessage') as FormControl;
  }
  get seatingPlan(): FormControl {
    return this.form.get('seatingPlan') as FormControl;
  }
  get seatingPlanType(): FormControl {
    return this.form.get('seatingPlanType') as FormControl;
  }
  get boardingPassType(): FormControl {
    return this.form.get('boardingPassType') as FormControl;
  }
  get boardingPass(): FormControl {
    return this.form.get('boardingPass') as FormControl;
  }
  get passengersNationality(): FormControl {
    return this.form.get('passengersNationality') as FormControl;
  }
  get kindOfPassengers(): FormControl {
    return this.form.get('kindOfPassengers') as FormControl;
  }
  get targetOfTheTrip(): FormControl {
    return this.form.get('targetOfTheTrip') as FormControl;
  }
  get mandatoryCNI(): FormControl {
    return this.form.get('mandatoryCNI') as FormControl;
  }
  get mandatoryPassport(): FormControl {
    return this.form.get('mandatoryPassport') as FormControl;
  }
  get onboardOther(): FormControl {
    return this.form.get('onboardOther') as FormControl;
  }
  get onBoardFlightAnnouncement(): FormGroup {
    return this.form.get('onBoardFlightAnnouncement') as FormGroup;
  }
  getOnBoardFlightAnnouncementByLanguage(language: EnumLanguage): FormControl {
    return this.onBoardFlightAnnouncement.get(language) as FormControl;
  }

  get counterCount(): FormControl {
    return this.form.get('counterCount') as FormControl;
  }

  get countersPerHour(): FormControl {
    return this.form.get('countersPerHour') as FormControl;
  }

  get headrestCovers(): FormControl {
    return this.form.get('headrestCovers') as FormControl;
  }

  get departTransferGATBoardingPass(): FormControl {
    return this.form.get('departTransferGATBoardingPass') as FormControl;
  }

  get arrivalTransferGATBoardingPass(): FormControl {
    return this.form.get('arrivalTransferGATBoardingPass') as FormControl;
  }

  get departTourLeader(): FormControl {
    return this.form.get('departTourLeader') as FormControl;
  }

  get arrivalTourLeader(): FormControl {
    return this.form.get('arrivalTourLeader') as FormControl;
  }

  get vipSeatingFront(): FormControl {
    return this.form.get('vipSeatingFront') as FormControl;
  }

  get departReducedMobilityPassengers(): FormControl {
    return this.form.get('departReducedMobilityPassengers') as FormControl;
  }

  get arrivalReducedMobilityPassengers(): FormControl {
    return this.form.get('arrivalReducedMobilityPassengers') as FormControl;
  }

  get oversizeBaggage(): FormControl {
    return this.form.get('oversizeBaggage') as FormControl;
  }

  get specialRemarks(): FormControl {
    return this.form.get('specialRemarks') as FormControl;
  }

  get languages(): EnumLanguage[] {
    return Object.values(EnumLanguage);
  }

  applyEnquiryFlight(): void {
    this.form = this.resetForm();

    if (this.enquiryFlight) {
      for (const field in this.form.value) {
        if (typeof this.enquiryFlight[field] !== 'undefined') {
          switch (field) {
            default:
              this.form.get(field).setValue(this.enquiryFlight[field]);
              break;
          }
        } else {
          switch (field) {
            case 'seatingPlanType':
              if (this.enquiryFlight.seatingPlan) {
                this.form.get(field).setValue(EnumEnquiryFlightSeatingPlan.other);
              }
              break;
            case 'boardingPassType':
              if (this.enquiryFlight.boardingPass) {
                this.form.get(field).setValue(EnumEnquiryFlightBoardingPass.other);
              }
              break;
          }
        }
      }

      this.updateOnBoardFlightAnnouncement();

      this.form.enable();
    }
  }

  resetForm(): FormGroup {
    const onBoardFlightAnnouncementFormGroup: FormGroup = new FormGroup({});

    for (const language of this.languages) {
      onBoardFlightAnnouncementFormGroup.addControl(language, new FormControl(null));
    }

    return new FormGroup({
      flightNumber: new FormControl(null),
      callsign: new FormControl(null),
      isFerryFlight: new FormControl(false),
      otherComment: new FormControl(null),
      checkInScreenMessage: new FormControl(null),
      seatingPlanType: new FormControl(EnumEnquiryFlightSeatingPlan.firstArrivedFirstServed),
      seatingPlan: new FormControl(null),
      boardingPassType: new FormControl(EnumEnquiryFlightBoardingPass.handedOverCheckInCounter),
      boardingPass: new FormControl(null),
      passengersNationality: new FormControl(null),
      kindOfPassengers: new FormControl(null),
      targetOfTheTrip: new FormControl(null),
      onboardOther: new FormControl(null),
      mandatoryCNI: new FormControl(true),
      mandatoryPassport: new FormControl(true),
      onBoardFlightAnnouncement: onBoardFlightAnnouncementFormGroup,
      counterCount: new FormControl(null),
      countersPerHour: new FormControl(null),
      headrestCovers: new FormControl(null),
      departTransferGATBoardingPass: new FormControl(null),
      arrivalTransferGATBoardingPass: new FormControl(null),
      departTourLeader: new FormControl(null),
      departTourLeaderPhone: new FormControl(null),
      arrivalTourLeader: new FormControl(null),
      arrivalTourLeaderPhone: new FormControl(null),
      vipSeatingFront: new FormControl(null),
      departReducedMobilityPassengers: new FormControl(null),
      arrivalReducedMobilityPassengers: new FormControl(null),
      oversizeBaggage: new FormControl(null),
      specialRemarks: new FormControl(null)
    });
  }

  prev(): void {
    this.goPrev.emit();
  }

  saveAndNext(): void {
    if (this.form.valid) {
      this.saveAndGoNext.emit(this.form.value);
    }
  }

  loadTranslation(): void {
    if (this.subscriptionsTranslations) {
      this.subscriptionsTranslations.unsubscribe();
    }

    this.subscriptionsTranslations = new Subscription();

    for (const language of this.languages) {
      this.subscriptionsTranslations.add(
        this.translateService.getTranslation(language).subscribe((translationObj: any) => {
          this.translationObj[language] = translationObj;

          this.updateOnBoardFlightAnnouncement();
        })
      );
    }
  }

  updateOnBoardFlightAnnouncement(force: boolean = false): void {
    for (const language of this.languages) {
      if (
        this.enquiryFlight &&
        this.translationObj[language] &&
        (force || !this.getOnBoardFlightAnnouncementByLanguage(language).value)
      ) {
        this.getOnBoardFlightAnnouncementByLanguage(language).setValue(
          this.translationObj[
            language
          ].FLIGHT_BRIEF.ONBOARD_INFORMATION.ON_BOARD_FLIGHT_ANNOUNCEMENT_VALUE.replace(
            '{{clientName}}',
            this.enquiryFlight.clientName
          )
            .replace('{{airportArrival}}', this.enquiryFlight.airportArrivalTitle)
            .replace('{{airlineTitle}}', this.enquiryFlight.airlineTitle)
            .replace('{{targetOfTheTrip}}', this.targetOfTheTrip.value || 'XXX')
        );

        this.getOnBoardFlightAnnouncementByLanguage(language).updateValueAndValidity();
      }
    }
  }
}
